import { saveAs } from 'file-saver';
import { chunk } from 'lodash';
import { loadExcelJS } from './spreadsheetLibraries';
import { removeSpecialCharsOfData } from './securityCheck';

const mapFormatToNumFmt = (format, formattingOptions) => {
  switch (format) {
    case 'number':
      return '0.00';
    case 'percentage':
      return '0.00 %';
    case 'currency':
      return `0.00 ${formattingOptions?.currency || ''}`;
    default:
      return '';
  }
};

const exportExcel = async ({
  baseData,
  name,
  title,
  detailedTable,
  extraSheetTitle,
  summaryColumns,
  showTitle = true,
  addExtraSheet = false,
  extraSheetData = [],
  autoFilter = true,
  showColumnsRowStyle = true,
  asCSV = false,
  columnsDef = [],
  formattingOptions,
  isDownload = true,
  onExportFinished
}) => {
  const workSheetName = (name || '').replace(/[*?:/\\[\]]/g, '').replace("'", '');

  const Excel = await loadExcelJS();
  const workbook = new Excel.Workbook();
  const worksheet = workbook.addWorksheet(workSheetName, { title: 'tet' });

  /* Security check */
  const data = removeSpecialCharsOfData(baseData);

  /* If there is no exportable data return {} so the export does not crash and exports an empty excel */
  const firstRow = data?.[0] || {};
  const headerKeys = Object.keys(detailedTable ? detailedTable[0] : firstRow);

  if (showTitle) {
    worksheet.getRow(1).values = [title ?? workSheetName];
    worksheet.getRow(1).alignment = { vertical: 'middle', horizontal: 'center' };
    worksheet.getCell('A1').font = { size: 18 };
    worksheet.mergeCells(1, 1, 1, headerKeys.length);
  }
  worksheet.getRow(showTitle ? 2 : 1).values = headerKeys;

  // todo: detailedTable is not in use. need to be removed.
  worksheet.columns = headerKeys.map((column) => ({
    // 'column' here is the value of the property of the first object
    // in the data array which is already translated to the user language
    key: column
  }));

  // Set the width of each column according to the widest content in that column
  worksheet.columns.forEach((column) => {
    let maxLength = 0;
    column.eachCell((cell) => {
      const length = cell.value ? cell.value.toString().length : 0;
      if (length > maxLength) {
        maxLength = length;
      }
    });
    // 'Header' here has the same translated value as the column key
    const columnDef = columnsDef.find(({ Header }) => Header === column.key);
    column.width = maxLength + 2;
    column.numFmt = mapFormatToNumFmt(columnDef?.format, formattingOptions);
  });

  const rows = (detailedTable || data).map((item) =>
    headerKeys.map((key) => {
      const currentItem = item[key];
      const def = columnsDef.find(({ Header }) => Header === key);
      if (def?.format === 'boolean') {
        return currentItem === undefined ? '' : currentItem ? 'Yes' : 'No';
      }
      return currentItem ?? '';
    })
  );

  // Use chunking approach for all datasets (handles both small and large efficiently)
  const CHUNK_SIZE = 1000; // Safe threshold to prevent stack overflow
  const startRow = showTitle ? 3 : 2;
  const chunks = chunk(rows, CHUNK_SIZE);

  // Process all data in chunks (works efficiently for small datasets too)
  let currentRowIndex = 0;
  for (const currentChunk of chunks) {
    // Insert at the correct position, accounting for previously inserted rows
    worksheet.insertRows(startRow + currentRowIndex, currentChunk);
    currentRowIndex += currentChunk.length;
  }
  if (addExtraSheet) {
    const extraSheet = workbook.addWorksheet(extraSheetTitle);

    extraSheet.getRow(1).values = Object.keys(extraSheetData[0]);
    extraSheet.columns = Object.keys(extraSheetData[0]).map((column) => ({
      key: column
    }));

    // Process extra sheet data in chunks to prevent stack overflow
    const extraSheetRows = extraSheetData.map((item) => Object.values(item));
    const extraSheetChunks = chunk(extraSheetRows, CHUNK_SIZE);

    // Process all data in chunks (works efficiently for small datasets too)
    let extraSheetRowIndex = 0;
    for (const currentChunk of extraSheetChunks) {
      // Insert at the correct position, accounting for previously inserted rows
      extraSheet.insertRows(2 + extraSheetRowIndex, currentChunk);
      extraSheetRowIndex += currentChunk.length;
    }
  }

  // filter in excel will be on
  if (autoFilter) {
    worksheet.autoFilter = {
      from: { row: showTitle ? 2 : 1, column: 1 },
      to: { row: showTitle ? 2 : 1, column: worksheet.columns.length }
    };
  }

  // Add a row of summary data
  if (summaryColumns) {
    const summaryRow = worksheet.addRow({});

    summaryColumns.forEach((columnName) => {
      let ranges = [];

      const column = worksheet.getColumn(columnName);

      column.eachCell((cell, rowNumber) => {
        if (![1, summaryRow.number].includes(rowNumber)) {
          ranges.push(cell.address);
        }
      });

      if (ranges.length > 0) {
        summaryRow.getCell(columnName).value = {
          formula: `SUM(${ranges[0]}:${ranges[ranges.length - 1]})`,
          result: 0
        };
      }
    });

    summaryRow.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'e8e8e8' }
    };

    summaryRow.font = {
      bold: true
    };
  }

  if (showColumnsRowStyle) {
    const row = worksheet.getRow(1);
    row.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'e8e8e8' }
    };

    row.font = {
      bold: true
    };
  }

  const fileName = `${workSheetName}.${asCSV ? 'csv' : 'xlsx'}`;

  if (isDownload) {
    workbook.xlsx.writeBuffer().then((bufferData) => {
      const blob = new Blob([bufferData], { type: 'application/octet-stream' });
      saveAs(blob, fileName);
    });

    onExportFinished?.();

    return;
  }

  // Return buffer so caller can add it and not download it immediately
  const bufferData = asCSV ? await workbook.csv.writeBuffer() : await workbook.xlsx.writeBuffer();

  return {
    buffer: bufferData,
    fileName,
    workbook,
    workSheetName
  };
};

export default exportExcel;
